import { endOfYesterday, startOfMonth, startOfWeek, subMonths } from "date-fns";
import {
  CalendarClock,
  CheckSquare,
  Clock,
  Home,
  MailCheck,
  MapPin,
  Tag,
  Thermometer,
  SlidersHorizontal,
  Users,
  Wallet,
  X,
} from "lucide-react";
import {
  FilterLiveForm,
  useCanAccess,
  useGetIdentity,
  useGetList,
  useListContext,
  useTranslate,
} from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SearchInput } from "@/components/admin";

import { FilterCategory } from "../filters/FilterCategory";
import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import { useIsMobile } from "@/hooks/use-mobile";
import { BUYER_OPTIONS } from "../funnel/BuyerProfile";
import { ActiveFilterButton } from "../misc/ActiveFilterButton";
import { AccountManagerFilter } from "../sales/AccountManagerInput";

export const ContactListFilter = () => {
  const isMobile = useIsMobile();
  if (!isMobile) return <ContactListFilterBar />;
  // on mobile, the header's global search replaces the name search
  return (
    <ResponsiveFilters>
      <FilterSections />
    </ResponsiveFilters>
  );
};

const chip =
  "h-8 w-auto rounded-full border border-border px-3 text-sm font-normal";

/**
 * Desktop: one row above the list. Search, the filters people use every day
 * as chips, everything else in a "Filters" popover, so the list keeps the
 * whole width and height of the screen.
 */
const ContactListFilterBar = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { filterValues, setFilters } = useListContext();
  const active = Object.keys(filterValues || {}).filter((key) => key !== "q");
  const hot = ["temperature", "sales_id"];
  const inMenu = active.filter((key) => !hot.includes(key)).length;
  const clear = () =>
    setFilters(filterValues?.q ? { q: filterValues.q } : {}, []);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterLiveForm formComponent={SearchForm}>
        <SearchInput
          source="q"
          placeholder={translate("resources.contacts.filters.search")}
        />
      </FilterLiveForm>
      <div className="flex items-center gap-1.5">
        {BUYER_OPTIONS.temperature.map((value) => (
          <ToggleFilterButton
            key={String(value)}
            className={chip}
            label={translate(`crm.buyer.temperature.${value}`)}
            value={{ temperature: value }}
          />
        ))}
        <span className="mx-1 h-5 w-px bg-border" />
        <ToggleFilterButton
          className={chip}
          label="resources.contacts.filters.managed_by_me"
          value={{ sales_id: identity?.id }}
        />
      </div>
      <Popover>
        <PopoverTrigger
          render={
            <Button variant="outline" size="sm" className="h-8 gap-1.5" />
          }
        >
          <SlidersHorizontal className="size-4" />
          {translate("crm.common.more_filters")}
          {inMenu > 0 && (
            <Badge className="h-5 min-w-5 px-1.5 text-xs">{inMenu}</Badge>
          )}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-[min(36rem,calc(100dvh-10rem))] w-[min(46rem,calc(100vw-18rem))] overflow-y-auto p-5"
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-5 lg:grid-cols-3">
            <FilterSections desktop />
          </div>
          {inMenu > 0 && (
            <div className="flex justify-end border-t pt-3">
              <Button variant="ghost" size="sm" onClick={clear}>
                {translate("ra.navigation.clear_filters")}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {active.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-muted-foreground"
          onClick={clear}
        >
          <X className="size-4" />
          {translate("ra.navigation.clear_filters")}
        </Button>
      )}
    </div>
  );
};

const SearchForm = (props: React.FormHTMLAttributes<HTMLFormElement>) => (
  <form className="w-72" {...props} />
);

/** Every filter group. On desktop temperature and "Me" live in the bar. */
const FilterSections = ({ desktop = false }: { desktop?: boolean }) => {
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const { canAccess: seesTeam } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  const { data } = useGetList("tags", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "name", order: "ASC" },
  });

  return (
    <>
      <FilterCategory
        label="resources.contacts.fields.last_seen"
        icon={<Clock />}
      >
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.today"
          value={{
            "last_seen@gte": endOfYesterday().toISOString(),
            "last_seen@lte": undefined,
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.this_week"
          value={{
            "last_seen@gte": startOfWeek(new Date()).toISOString(),
            "last_seen@lte": undefined,
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.before_this_week"
          value={{
            "last_seen@gte": undefined,
            "last_seen@lte": startOfWeek(new Date()).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.before_this_month"
          value={{
            "last_seen@gte": undefined,
            "last_seen@lte": startOfMonth(new Date()).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.before_last_month"
          value={{
            "last_seen@gte": undefined,
            "last_seen@lte": subMonths(
              startOfMonth(new Date()),
              1,
            ).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>

      {!desktop && (
        <>
          <FilterCategory
            label="crm.buyer.temperature_label"
            icon={<Thermometer />}
          >
            {BUYER_OPTIONS.temperature.map((value) => (
              <ToggleFilterButton
                key={String(value)}
                className="w-auto md:w-full justify-between h-10 md:h-8"
                label={translate(`crm.buyer.temperature.${value}`)}
                value={{ temperature: value }}
                size={isMobile ? "lg" : undefined}
              />
            ))}
          </FilterCategory>
        </>
      )}
      <FilterCategory label="crm.buyer.payment_method.label" icon={<Wallet />}>
        {BUYER_OPTIONS.payment_method.map((value) => (
          <ToggleFilterButton
            key={String(value)}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={translate(`crm.buyer.payment_method.${value}`)}
            value={{ payment_method: value }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>
      <FilterCategory
        label="crm.buyer.buy_timing.label"
        icon={<CalendarClock />}
      >
        {BUYER_OPTIONS.buy_timing.map((value) => (
          <ToggleFilterButton
            key={String(value)}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={translate(`crm.buyer.buy_timing.${value}`)}
            value={{ buy_timing: value }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>
      <FilterCategory label="crm.buyer.residence.label" icon={<MapPin />}>
        {BUYER_OPTIONS.residence.map((value) => (
          <ToggleFilterButton
            key={String(value)}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={translate(`crm.buyer.residence.${value}`)}
            value={{ residence: value }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>
      <FilterCategory label="crm.buyer.rooms_label" icon={<Home />}>
        {[1, 2, 3].map((value) => (
          <ToggleFilterButton
            key={value}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={translate("crm.funnel.rooms", { count: value })}
            value={{ rooms_wanted: value }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>
      <FilterCategory label="crm.buyer.consent" icon={<MailCheck />}>
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="crm.buyer.consent_given"
          value={{ marketing_consent: true }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>

      <FilterCategory label="resources.contacts.filters.tags" icon={<Tag />}>
        {data &&
          data.map((record) => (
            <ToggleFilterButton
              className="w-auto md:w-full justify-between h-10 md:h-8"
              key={record.id}
              label={
                <Badge
                  variant="secondary"
                  className="text-black text-sm md:text-xs font-normal cursor-pointer"
                  style={{
                    backgroundColor: record?.color,
                  }}
                >
                  {record?.name}
                </Badge>
              }
              value={{ "tags@cs": `{${record.id}}` }}
              size={isMobile ? "lg" : undefined}
            />
          ))}
      </FilterCategory>

      <FilterCategory
        icon={<CheckSquare />}
        label="resources.contacts.filters.tasks"
      >
        <ToggleFilterButton
          className="w-full justify-between h-10 md:h-8"
          label="resources.tasks.filters.with_pending"
          value={{ "nb_tasks@gt": 0 }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>

      {(!desktop || seesTeam) && (
        <FilterCategory
          icon={<Users />}
          label="resources.contacts.fields.sales_id"
        >
          {!desktop && (
            <ToggleFilterButton
              className="w-auto md:w-full max-w-full justify-between h-10 md:h-8"
              label="crm.common.me"
              value={{ sales_id: identity?.id }}
              size={isMobile ? "lg" : undefined}
            />
          )}
          <AccountManagerFilter
            className="w-auto md:w-full max-w-full justify-between h-10 md:h-8"
            size={isMobile ? "lg" : undefined}
          />
        </FilterCategory>
      )}
    </>
  );
};

export const ContactListFilterSummary = () => {
  const { noteStatuses } = useConfigurationContext();
  const { identity } = useGetIdentity();
  const { data } = useGetList("tags", {
    pagination: { page: 1, perPage: 10 },
    sort: { field: "name", order: "ASC" },
  });
  const { filterValues } = useListContext();
  const hasFilters = !!Object.entries(filterValues || {}).filter(
    ([key]) => key !== "q",
  ).length;

  if (!hasFilters) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-start mb-4 gap-1">
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.today"
        value={{
          "last_seen@gte": endOfYesterday().toISOString(),
          "last_seen@lte": undefined,
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.this_week"
        value={{
          "last_seen@gte": startOfWeek(new Date()).toISOString(),
          "last_seen@lte": undefined,
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.before_this_week"
        value={{
          "last_seen@gte": undefined,
          "last_seen@lte": startOfWeek(new Date()).toISOString(),
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.before_this_month"
        value={{
          "last_seen@gte": undefined,
          "last_seen@lte": startOfMonth(new Date()).toISOString(),
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.before_last_month"
        value={{
          "last_seen@gte": undefined,
          "last_seen@lte": subMonths(startOfMonth(new Date()), 1).toISOString(),
        }}
      />

      {noteStatuses.map((status) => (
        <ActiveFilterButton
          key={status.value}
          className="w-auto justify-between h-8"
          label={
            <span>
              {status.label} <Status status={status.value} />
            </span>
          }
          value={{ status: status.value }}
        />
      ))}

      {data &&
        data.map((record) => (
          <ActiveFilterButton
            className="w-auto justify-between h-8"
            key={record.id}
            label={
              <Badge
                variant="secondary"
                className="text-black text-sm md:text-xs font-normal cursor-pointer"
                style={{
                  backgroundColor: record?.color,
                }}
              >
                {record?.name}
              </Badge>
            }
            value={{ "tags@cs": `{${record.id}}` }}
          />
        ))}

      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.tasks.filters.with_pending"
        value={{ "nb_tasks@gt": 0 }}
      />

      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.managed_by_me"
        value={{ sales_id: identity?.id }}
      />
    </div>
  );
};
